import React, { useEffect } from 'react'
import moment from 'moment'
import { Cascader, Select } from 'antd'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms/button'
import { DatePicker } from '@/components/organisms/customer/sales-manager/date-picker'
import { DiscountSettingType, PaymentSettingType, SalesDiscountType } from '@/models/sales'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { ReservationType } from '@/models/reservation'
import { getRoomCascadeOptionsByReservations } from '@/utils/reservations'
import { initializeSelectBoxStyle } from '@/constants/form'
import '@/components/molecules/customer/sales-manager/usage-detail/ant-select.css'
import { unitPriceStyle } from '@/components/molecules/customer/sales-manager/discount/not-confirmed-discount'
import { cascaderNameSearch } from '@/libs/cascader-name-search'

type PostClosingConfirmedDiscountProps = {
  index: number
  discount: SalesDiscountType
  advanceCompany?: any
  onMarkDelete: () => void
  onCancelEdit: () => void
  onSaveEdit: () => void
  pendingDelete?: boolean
  salesDiscounts: DiscountSettingType[]
  salesDiscountPayments: PaymentSettingType[]
  confirmedDay: string
  changePostClosingDate: (date: moment.Moment | null, index: number) => void
  changePostClosingState: (e: { target: { value: string; name: string } }, index: number) => void
  accountsReceivableSettings: AccountsReceivableSettingType[]
  reservations?: ReservationType[]
  currentTabReservationId?: string
}

export const PostClosingConfirmedDiscount: React.FC<PostClosingConfirmedDiscountProps> = ({
  index,
  discount,
  advanceCompany,
  onMarkDelete,
  onCancelEdit,
  onSaveEdit,
  pendingDelete = false,
  salesDiscounts,
  salesDiscountPayments,
  confirmedDay,
  changePostClosingDate,
  changePostClosingState,
  accountsReceivableSettings,
  reservations,
  currentTabReservationId,
}) => {
  const { t } = useTranslation()

  const newAdvanceCompany = advanceCompany?.paymentCompanies?.map(item => ({
    value: item?.id,
    label: item?.name,
    disabled: !item.currentBalance,
  }))
  const salesPaymentOptions =
    salesDiscountPayments?.map(item => {
      if (Object.keys(advanceCompany?.paymentMethod || {}).length && item?.id === advanceCompany?.paymentMethod?.id) {
        return { value: item?.id, label: item?.name, children: newAdvanceCompany }
      }
      return { value: item?.id, label: item?.name }
    }) || []

  const reservation = reservations?.find(r => r.reservationId === currentTabReservationId)
  const roomsOptions = getRoomCascadeOptionsByReservations({ reservation, reservations })
  const displayRender = (label: string[]) => label[label.length - 1]
  const salesDiscountIds = salesDiscounts.map(d => d.id!)

  useEffect(() => {
    if (!discount.reservationId && currentTabReservationId) {
      changePostClosingState({ target: { value: currentTabReservationId, name: 'reservationId' } }, index)
    }
    if (discount.salesAccountsReceivableMasterId == null) {
      changePostClosingState({ target: { value: '', name: 'salesAccountsReceivableMasterId' } }, index)
    }
  }, [currentTabReservationId, discount.reservationId, discount.salesAccountsReceivableMasterId, changePostClosingState, index])

  const editActionsRow = (
    <tr css={editActionsRowStyle}>
      {pendingDelete ? (
        <td colSpan={7} css={editActionsCellStyle}>
          <div css={editActionsWrapperStyle}>
            <Button buttonType={3} width={80} height={32} fontSize={12} marginRight={16} onClick={onCancelEdit}>
              {t('Cancel')}
            </Button>
            <Button buttonType={1} width={80} height={32} fontSize={12} onClick={onSaveEdit}>
              {t('Save')}
            </Button>
          </div>
        </td>
      ) : (
        <>
          <td colSpan={5} css={editActionsSpacerStyle} />
          <td colSpan={2} css={editActionsCellStyle}>
            <div css={editActionsWrapperStyle}>
              <Button buttonType={3} width={80} height={32} fontSize={12} marginRight={16} onClick={onCancelEdit}>
                {t('Cancel')}
              </Button>
              <Button buttonType={1} width={80} height={32} fontSize={12} onClick={onSaveEdit}>
                {t('Save')}
              </Button>
            </div>
          </td>
        </>
      )}
    </tr>
  )

  if (pendingDelete) {
    return editActionsRow
  }

  return (
    <>
      <tr css={editRowStyle}>
        <td>
          <DatePicker
            date={discount.salesDate ? moment(discount.salesDate) : null}
            setDate={value => {
              const currentDate = discount.salesDate ? moment(discount.salesDate) : null
              const resolved = typeof value === 'function' ? value(currentDate) : value
              changePostClosingDate(resolved, index)
            }}
            confirmedDay={confirmedDay}
            allowClosedDates={true}
            inputId={`post-closing-discount-date-${index}`}
            initialVisibleMonth={() => moment(discount.salesDate || undefined)}
          />
        </td>
        <td>
          <select
            name="salesDiscountId"
            css={editModeLargeSelectBoxStyle}
            value={discount.salesDiscountId}
            onChange={e => changePostClosingState(e, index)}
          >
            {!discount.salesDiscountId && <option value="">{t('Please select')}</option>}
            {discount.salesDiscountId && salesDiscountIds.indexOf(discount.salesDiscountId) === -1 && (
              <option value={discount.salesDiscountId}>{discount.salesDiscountName}</option>
            )}
            {salesDiscounts.map(salesDiscount => (
              <option key={salesDiscount.id} value={salesDiscount.id}>
                {salesDiscount.name}
              </option>
            ))}
          </select>
        </td>
        <td>
          <Select
            onChange={value => {
              const reservationId = roomsOptions?.find(room => room.value === value)?.reservationId
              changePostClosingState({ target: { value: reservationId ?? '', name: 'reservationId' } }, index)
              changePostClosingState({ target: { value: value as string, name: 'guestRoomAssignId' } }, index)
            }}
            value={discount?.guestRoomAssignId || undefined}
            suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
            placeholder={t('Please select')}
          >
            {roomsOptions.map(room => (
              <Select.Option key={room.value} value={room.value}>
                {room.label}
              </Select.Option>
            ))}
          </Select>
        </td>
        <td>
          <Cascader
            options={salesPaymentOptions}
            expandTrigger="hover"
            className="cascader-customize"
            value={discount.companyId ? [discount.salesPaymentId, discount.companyId] : [discount.salesPaymentId]}
            displayRender={displayRender}
            popupClassName="popup-cascader-customize"
            showSearch={cascaderNameSearch}
            onChange={async value => {
              await changePostClosingState({ target: { value: value[0] as string, name: 'salesPaymentId' } }, index)
              changePostClosingState({ target: { value: value[1] ? (value[1] as string) : '', name: 'companyId' } }, index)
            }}
            allowClear={false}
            suffixIcon={<span></span>}
          />
        </td>
        <td>
          <input
            name="salesSubjectPrice"
            type="text"
            css={unitPriceStyle}
            value={discount.salesSubjectPrice}
            onChange={e => changePostClosingState(e, index)}
            placeholder="0000"
            className="unitPriceStyle"
          />
        </td>
        <td>
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={true}
            suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
            onChange={value => {
              changePostClosingState({ target: { value: value as string, name: 'salesAccountsReceivableMasterId' } }, index)
            }}
            value={discount.salesAccountsReceivableMasterId}
          >
            <Select.Option value="">-</Select.Option>
            {accountsReceivableSettings.map((accountsReceivable, arIndex) => (
              <Select.Option key={arIndex} value={accountsReceivable.id}>
                {accountsReceivable.name}
              </Select.Option>
            ))}
            {discount.salesAccountsReceivableMasterId !== '' &&
              !accountsReceivableSettings.some(ar => ar.id === discount.salesAccountsReceivableMasterId) && (
                <Select.Option value={discount.salesAccountsReceivableMasterId}>{discount.salesAccountsReceivableName}</Select.Option>
              )}
          </Select>
        </td>
        <td css={actionCellStyle}>
          <button type="button" css={iconButtonStyle} onClick={onMarkDelete} aria-label={t('Delete')}>
            <img alt="close" src={require('@/static/images/cancel.svg')} css={closeIconStyle} />
          </button>
        </td>
      </tr>
      {editActionsRow}
    </>
  )
}

const editRowStyle = css({
  td: {
    height: 57,
    verticalAlign: 'middle',
    borderBottom: 'none',
  },
})

const editActionsRowStyle = css({
  td: {
    borderTop: 'none',
    paddingTop: 0,
    paddingBottom: 12,
    verticalAlign: 'top',
  },
})

const editActionsSpacerStyle = css({
  padding: 0,
})

const editActionsCellStyle = css({
  textAlign: 'right',
  paddingRight: 8,
})

const editActionsWrapperStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
})

const actionCellStyle = css({
  textAlign: 'right',
  verticalAlign: 'middle',
  paddingRight: 8,
})

const iconButtonStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
})

const closeIconStyle = css({
  width: 16,
  height: 16,
})

const selectBoxStyle = css(initializeSelectBoxStyle, {
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  display: 'block',
  paddingLeft: 14,
  paddingRight: 14,
  marginRight: 10,
})

const largeSelectBoxStyle = css(selectBoxStyle, {
  marginRight: '1.5%',
  width: '90%',
  backgroundPosition: '95% center',
})

const editModoSelectBoxStyle = css(selectBoxStyle, {
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
})

const editModeLargeSelectBoxStyle = css(editModoSelectBoxStyle, largeSelectBoxStyle)
